import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";

// Import your components
import RetailerForm from "../components/RetailerForm";
import Card from "../components/Card";
import BlueButton from "../components/BlueButton";
import { useNavigate } from "react-router-dom";


export default function Dashboard() {

  const navigate = useNavigate();
  
  const [showAdd, setShowAdd] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showRaiseTicket, setShowRaiseTicket] = useState(false);

  const [kpis, setKpis] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const authUserString = sessionStorage.getItem("authUserData");
  const authUser = JSON.parse(authUserString);
  const loginUserID = authUser?.id;
  const CompanyID = authUser?.company_id;
 

  // === Fetch Dashboard KPIs ===
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await axios.get(
          `https://goelectronix.in/api/v1/distributor-dashboard?org_id=${loginUserID}`
        );

        const data = res.data;
        const balances = data.balances || [];
        const totalRegular = balances.reduce(
          (sum, item) => sum + parseInt(item.regular_qty || 0, 10),
          0
        );
        const totalSmart = balances.reduce(
          (sum, item) => sum + parseInt(item.smart_qty || 0, 10),
          0
        );
        const retailerCount = data.retailer_count || 0;

        setKpis([
          { label: "Retailers", value: retailerCount, sub: `+0 this week` },
          { label: "Regular Keys (Avail)", value: totalRegular, sub: "From Company" },
          { label: "Smart Keys (Avail)", value: totalSmart, sub: "From Company" },
        ]);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      }
    };

    fetchDashboard();
  }, [loginUserID]);

  // === Fetch Key Assignments ===
  useEffect(() => {
    const fetchAssignments = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `https://goelectronix.in/api/v1/keys/report?sender_id=${loginUserID}&mode=1&company_id=${CompanyID}&page=${page}&per_page=10`
        );

        const data = res.data;
        // ✅ Sort latest first by created_at
        const sorted = (data.data || []).sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        setAssignments(sorted);
        setLastPage(data.last_page);
      } catch (error) {
        console.error("Failed to fetch assignments:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAssignments();
  }, [loginUserID, CompanyID, page]);

  return (
    <div>
      {/* KPI Cards */}
      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k, idx) => {
          const handleClick = () => {
            if (k.label === "Retailers") {
              navigate("/allretailers");
            } else if (k.label.includes("Regular Keys") || k.label.includes("Smart Keys")) {
              navigate("/inventory");
            }
          };

          return (
            <motion.div
              key={k.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * idx }}
              onClick={handleClick}
              className="cursor-pointer rounded-2xl border bg-white p-3 sm:p-4 shadow-sm hover:shadow-md transition"
            >
              <p className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">
                {k.label}
              </p>

              <p
                className="mt-1 sm:mt-2 text-xl sm:text-2xl font-semibold"
                style={{ color: "#0A3E91" }}
              >
                {loading ? (
                  <span className="inline-block w-10 h-5 bg-gray-200 animate-pulse rounded"></span>
                ) : (
                  k.value
                )}
              </p>

              <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-slate-500">
                {k.sub}
              </p>
            </motion.div>
          );
        })}
      </div>



      {/* Recent Assignments + Actions */}
      <section className="mt-4 sm:mt-6 grid grid-cols-1 gap-3 lg:grid-cols-3">
        {/* Recent Key Assignments */}
        <div className="lg:col-span-2 rounded-2xl border bg-white shadow-sm p-3 sm:p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold">Recent Key Assignments</h2>
            <button
              onClick={() => toast.info("Coming Soon 🚀")}
              className="rounded-lg px-3 py-1.5 text-sm font-semibold text-white"
              style={{ backgroundColor: "#0B5ED7" }}
            >
              Export
            </button>
          </div>

          <div className="overflow-y-auto max-h-72">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-100 text-left text-xs text-slate-500">
                <tr>
                  <th className="p-2">Date</th>
                  <th className="p-2">Retailer (Code)</th>
                  <th className="p-2">Type</th>
                  <th className="p-2">Package</th>
                  <th className="p-2">Count</th>
                  <th className="p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="p-4 text-center text-slate-500">
                      Loading...
                    </td>
                  </tr>
                ) : assignments.length > 0 ? (
                  assignments.map((a) => (
                    <tr key={a.id} className="border-t">
                      <td className="p-2">
                        {new Date(a.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-2">
                        {a.receiver_name} ({a.receiver_code})
                      </td>
                      <td className="p-2">
                        {a.key_type === 1 ? "Regular" : "Smart"}
                      </td>
                      <td className="p-2">{a.package_name}</td>
                      <td className="p-2">{a.qty}</td>
                      <td className="p-2">
                        <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700">
                          Completed
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="p-4 text-center text-slate-500">
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-center mt-3 gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 rounded border text-sm disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-sm">
              Page {page} of {lastPage}
            </span>
            <button
              disabled={page === lastPage}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 rounded border text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>

        {/* Right Side Quick Actions + Payout Summary */}
        <div className="space-y-3 sm:space-y-4">
          {/* Quick Actions */}
          <div className="rounded-2xl border bg-white shadow-sm p-3 sm:p-4">
            <h2 className="text-sm font-semibold mb-2">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setShowAdd(true)}
                className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
                style={{ backgroundColor: "#0B5ED7" }}
              >
                <Plus className="mr-1" size={14} /> Add Retailer
              </button>
               <button
                  onClick={() => navigate("/keys")}
                  className="rounded-lg px-3 py-2 text-sm font-semibold text-white"
                  style={{ backgroundColor: "#0B5ED7" }}
                >
                  Assign Keys
                </button>
              <button
                onClick={() => navigate("/keys", { state: { tab: "receive" } })}
                className="rounded-lg px-3 py-2 text-sm font-semibold text-white"
                style={{ backgroundColor: "#0B5ED7" }}
              >
                Receive Keys
              </button>
              <button
                onClick={() => navigate("/support")}
                className="rounded-lg px-3 py-2 text-sm font-semibold text-white"
                style={{ backgroundColor: "#E11D48" }}
              >
                Raise Ticket
              </button>
            </div>
          </div>

          {/* Payout Summary */}
          {/* <div className="rounded-2xl border bg-white shadow-sm p-3 sm:p-4">
            <h2 className="text-sm font-semibold mb-2">Payout Summary</h2>
            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span>This Week</span>
                <span className="font-semibold">₹ 1.8 L</span>
              </div>
              <div className="flex justify-between">
                <span>Collected</span>
                <span className="font-semibold">₹ 1.2 L</span>
              </div>
              <div className="flex justify-between">
                <span>Outstanding</span>
                <span className="font-semibold">₹ 0.6 L</span>
              </div>
            </div>
          </div> */}

          <div className="rounded-2xl border bg-white shadow-sm p-3 sm:p-4">
            <h2 className="text-sm font-semibold mb-2">Payout Summary</h2>
            <div className="flex items-center justify-center h-20">
              <span className="text-gray-500 font-semibold">🚀 Coming Soon</span>
            </div>
          </div>

        </div>
      </section>

      {/* -------------------- Modals -------------------- */}

      {/* Add Retailer Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#0A3E91]">Add Retailer</h3>
              <button
                onClick={() => setShowAdd(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <RetailerForm
              onCancel={() => setShowAdd(false)}
              onSubmit={() => setShowAdd(false)}
            />
          </div>
        </div>
      )}

      {/* Assign Keys Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#0A3E91]">
                Assign Keys
              </h3>
              <button
                onClick={() => setShowAssignModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <Card title="Assign Keys to Retailer">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Retailer ID / Code - empty */}
                <input
                  placeholder="Retailer ID / Code"
                  className="rounded-lg border px-3 py-2 text-sm w-full"
                />

                {/* Retailer Name - empty */}
                <input
                  placeholder="Retailer Name"
                  className="rounded-lg border px-3 py-2 text-sm w-full"
                />

                {/* Key type */}
                <select className="rounded-lg border px-3 py-2 text-sm w-full">
                  <option value="regular">Regular</option>
                  <option value="smart">Smart</option>
                </select>

                {/* Key package */}
                <select className="rounded-lg border px-3 py-2 text-sm w-full">
                  <option>Regular Key</option>
                  <option>Regular + Warranty</option>
                  <option>Smart Key</option>
                </select>

                {/* Start Key */}
                <input
                  placeholder="Start Key (e.g., GE-1000)"
                  className="rounded-lg border px-3 py-2 text-sm w-full"
                />

                {/* End Key */}
                <input
                  placeholder="End Key (e.g., GE-1050)"
                  className="rounded-lg border px-3 py-2 text-sm w-full"
                />

                {/* Submit button */}
                <div className="sm:col-span-2 flex justify-end">
                  <BlueButton>Assign</BlueButton>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}


      {/* Raise Ticket Modal */}
      {showRaiseTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#0A3E91]">Raise a Ticket</h3>
              <button
                onClick={() => setShowRaiseTicket(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <Card title="Raise a Ticket">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <select className="rounded-lg border px-3 py-2 text-sm">
                  <option>Key Issue</option>
                  <option>Payment Issue</option>
                  <option>Inventory Issue</option>
                  <option>Other</option>
                </select>
                <input
                  placeholder="Subject"
                  className="rounded-lg border px-3 py-2 text-sm"
                />
                <textarea
                  placeholder="Describe the issue"
                  className="sm:col-span-2 min-h-[120px] rounded-lg border px-3 py-2 text-sm"
                />
                <div className="sm:col-span-2 flex items-center justify-between">
                  <input type="file" className="text-sm" />
                  <BlueButton>Submit Ticket</BlueButton>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}


// import React from "react";
// import { motion } from "framer-motion";
// import { Plus } from "lucide-react";

// export default function Dashboard() {
//   const kpis = [
//     { label: "Retailers", value: "128", sub: "+5 this week" },
//     { label: "Regular Keys (Avail)", value: "242", sub: "From Company" },
//     { label: "Smart Keys (Avail)", value: "170", sub: "From Company" },
//     { label: "Pending Payments", value: "₹ 6.2 L", sub: "41 retailers" },
//   ];

//   return (
//     <div>
//       {/* KPI Cards */}
//       <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
//         {kpis.map((k, idx) => (
//           <motion.div
//             key={k.label}
//             initial={{ opacity: 0, y: 12 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ delay: 0.05 * idx }}
//             className="rounded-2xl border bg-white p-3 sm:p-4 shadow-sm"
//           >
//             <p className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">
//               {k.label}
//             </p>
//             <p
//               className="mt-1 sm:mt-2 text-xl sm:text-2xl font-semibold"
//               style={{ color: "#0A3E91" }}
//             >
//               {k.value}
//             </p>
//             <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-slate-500">
//               {k.sub}
//             </p>
//           </motion.div>
//         ))}
//       </div>

//       {/* Recent Assignments + Actions */}
//       <section className="mt-4 sm:mt-6 grid grid-cols-1 gap-3 lg:grid-cols-3">
//         {/* Recent Key Assignments */}
//         <div className="lg:col-span-2 rounded-2xl border bg-white shadow-sm p-3 sm:p-4">
//           <div className="flex items-center justify-between mb-3">
//             <h2 className="text-sm font-semibold">Recent Key Assignments</h2>
//             <button
//               className="rounded-lg px-3 py-1.5 text-sm font-semibold text-white"
//               style={{ backgroundColor: "#0B5ED7" }}
//             >
//               Export
//             </button>
//           </div>
//           <table className="w-full text-sm">
//             <thead className="text-left text-xs text-slate-500">
//               <tr>
//                 <th className="p-2">Date</th>
//                 <th className="p-2">Retailer (Code)</th>
//                 <th className="p-2">Type</th>
//                 <th className="p-2">Package</th>
//                 <th className="p-2">Key Range</th>
//                 <th className="p-2">Count</th>
//                 <th className="p-2">Status</th>
//               </tr>
//             </thead>
//             <tbody>
//               <tr className="border-t">
//                 <td className="p-2">2025-09-22</td>
//                 <td className="p-2">Shop 101 (RT-1001)</td>
//                 <td className="p-2">Regular</td>
//                 <td className="p-2">Regular + Warranty</td>
//                 <td className="p-2">GE-1000…GE-1020</td>
//                 <td className="p-2">21</td>
//                 <td className="p-2">
//                   <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700">
//                     Completed
//                   </span>
//                 </td>
//               </tr>
              // <tr className="border-t">
              //   <td className="p-2">2025-09-22</td>
              //   <td className="p-2">Shop 203 (RT-1002)</td>
              //   <td className="p-2">Smart</td>
              //   <td className="p-2">Smart Key</td>
              //   <td className="p-2">GE-1021…GE-1030</td>
              //   <td className="p-2">10</td>
              //   <td className="p-2">
              //     <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700">
              //       Completed
              //     </span>
              //   </td>
              // </tr>
              // <tr className="border-t">
              //   <td className="p-2">2025-09-21</td>
              //   <td className="p-2">Shop 118 (RT-1003)</td>
              //   <td className="p-2">Regular</td>
              //   <td className="p-2">Regular Key</td>
              //   <td className="p-2">GE-0900…GE-0925</td>
              //   <td className="p-2">26</td>
              //   <td className="p-2">
              //     <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-700">
              //       Pending
              //     </span>
              //   </td>
              // </tr>
//             </tbody>
//           </table>
//         </div>

//         {/* Right Side Quick Actions + Payout Summary */}
//         <div className="space-y-3 sm:space-y-4">
//           {/* Quick Actions */}
//           <div className="rounded-2xl border bg-white shadow-sm p-3 sm:p-4">
//             <h2 className="text-sm font-semibold mb-2">Quick Actions</h2>
//             <div className="grid grid-cols-2 gap-2">
//               <button
//                 className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
//                 style={{ backgroundColor: "#0B5ED7" }}
//               >
//                 <Plus className="mr-1" size={14} /> Add Retailer
//               </button>
//               <button
//                 className="rounded-lg px-3 py-2 text-sm font-semibold text-white"
//                 style={{ backgroundColor: "#0B5ED7" }}
//               >
//                 Assign Keys
//               </button>
//               <button
//                 className="rounded-lg px-3 py-2 text-sm font-semibold text-white"
//                 style={{ backgroundColor: "#0B5ED7" }}
//               >
//                 Receive Keys
//               </button>
//               <button
//                 className="rounded-lg px-3 py-2 text-sm font-semibold text-white"
//                 style={{ backgroundColor: "#E11D48" }}
//               >
//                 Raise Ticket
//               </button>
//             </div>
//           </div>

//           {/* Payout Summary */}
//           <div className="rounded-2xl border bg-white shadow-sm p-3 sm:p-4">
//             <h2 className="text-sm font-semibold mb-2">Payout Summary</h2>
//             <div className="text-sm space-y-1">
//               <div className="flex justify-between">
//                 <span>This Week</span>
//                 <span className="font-semibold">₹ 1.8 L</span>
//               </div>
//               <div className="flex justify-between">
//                 <span>Collected</span>
//                 <span className="font-semibold">₹ 1.2 L</span>
//               </div>
//               <div className="flex justify-between">
//                 <span>Outstanding</span>
//                 <span className="font-semibold">₹ 0.6 L</span>
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>
//     </div>
//   );
// }
